import React, { useEffect, useState } from "react";
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form/dist/react-hook-form.ie11';
import { object, string } from 'yup';
import { Container, Form, Row, Col } from "react-bootstrap";
import { CircularProgress, ThemeProvider, createTheme, makeStyles, Button, SvgIcon } from '@material-ui/core';
import { Helmet } from "react-helmet";
import ProductDetails from "../../MCT/Helpers/MCTProductDetails";
import { CheckboxInput, TextInput } from '../../../Helpers/Inputs';
import CovrButton from '../../../Helpers/Buttons/CovrButton';
import FeatureCard from "../Cards/FeatureCard";
import InputTheme from '../Helpers/InputTheme';
import { CurrencyFormat, InputMaskPhone } from "../../../Helpers/Formatters";
import { ProductDetailsDialog } from "../Cards/MCTQuoteCard";
import CheckmarkIcon from "../../../../icons/checkmark.svg";
import { QuoteProductID } from "../QuoteProductID";
import { NextStep } from "./NextStep";
import { ProductDetailsDialog as TermProductDetailsDialog } from "../../CustomizeTerm/QuoterComponents/ProductDetailModel";
import './InsuranceRequest.scss';
import { CurrencyFormatFunc } from "../../../Helpers/Formatters/CurrencyFormat";

const useStyles = makeStyles(theme => ({
  backButton: {
    color: theme.palette.primary.main,
    fontWeight: 700,
    letterSpacing: 1.4,
    boxShadow: "0 2px 3px rgba(0, 0, 0, 0.161)",
  },
  submitButton: {
    fontWeight: 700,
    letterSpacing: 1.4,
  },
  detailButton: {
    padding: 0,
    fontSize: 14,
    fontWeight: 700,
    color: theme.palette.primary.main,
    textTransform: "uppercase",
    lineHeight: "24px",
  },
  ratingText: {
    color: theme.palette.primary.main,
  },
  lightBackground: {
    backgroundColor: `${theme.palette.primary.main}16`,
  },
}));

export default function InsuranceRequest(props) {
  const [isProductDetailsDialogOpen, setIsProductDetailsDialogOpen] = useState(false);

  const { register, errors, handleSubmit } = useForm({
    validationSchema: object().shape({
      phone: props.enablePhone ? string().required("Phone Number is required.")
        .matches(/^\(\d{3}\)\s\d{3}-\d{4}$/, 'Phone must be formatted as (123) 456-7890') : string(),
      email: string()
        .email('Email address must be valid.')
        .required('Email is required'),
      firstName: string()
        .matches(/^\s*$|^[ a-zA-ZÀ-ÖØ-öø-ÿ'.-]+$/, 'Numbers not allowed.')
        .required('First Name is required.'),
      lastName: string()
        .matches(/^\s*$|^[ a-zA-ZÀ-ÖØ-öø-ÿ'.-]+$/, 'Numbers not allowed.')
        .required('Last Name is required.'),
    }),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [disableContinueButton, setDisableContinueButton] = useState(false);

  useEffect(() => {
    if (props.quoterFormState.carrierName.toLowerCase() === 'haven') {
      setDisableContinueButton(true);
    }
   if(props.basicInfo) {
    setFirstName(props.basicInfo.firstName);
    setLastName(props.basicInfo.lastName);
    setEmail(props.basicInfo.email);
   }
  }, []);

  const onSubmit = () => {
    setLoading(true);
    props.onSubmit(email, firstName, lastName, phone, setLoading);
  };

  const handleAgreeChecked = () => {
    if (!agreed) {
      setAgreed(true);
    } else {
      setAgreed(false);
    }

    if (!agreed) {
      setDisableContinueButton(false);
    } else {
      setDisableContinueButton(true);
    }
  };

  return (
    <ThemeProvider theme={(theme) => createTheme({
      ...theme,
      ...InputTheme(),
    })}>
      <div>
        <Helmet>
          <title>Insurance Request</title>
        </Helmet>
        <Container className="request-wrapper">
          <Row className="request-container d-flex">
            <ReviewContainer quote={props.quoterFormState} isProductDetailsDialogOpen={isProductDetailsDialogOpen} setIsProductDetailsDialogOpen={setIsProductDetailsDialogOpen}
             planFeature={props.planFeature} isHavenLifeSIProduct={props.isHavenLifeSIProduct} isHavenTermProduct={props.isHavenTermProduct}/>
            <Col md={6} className="d-flex justify-content-center">
              <div className="contact-info-container">
                <Row className="flex-column align-items-center">
                  <div className={`icon-container ${classes.lightBackground}`}>
                    <SvgIcon component={CheckmarkIcon} color='primary' />
                  </div>
                  <h2 className="request-title text-center">Let’s start your online application</h2>
                </Row>
                <Row className="justify-content-center">
                <p className="text-center request-line">Get {props.isHavenLifeSIProduct || props.isHavenTermProduct ? 'your decision' : 'approved'} in ten minutes</p>
                </Row>
                <Form onSubmit={handleSubmit(onSubmit)}>
                  <Row className="justify-content-center">
                    <Col lg={12}>
                      <Form.Group>
                        <TextInput
                          name="firstName"
                          id="firstName"
                          label="First Name"
                          value={firstName}
                          onChange={e => setFirstName(e.target.value)}
                          inputRef={register}
                          errors={errors.firstName}
                        />
                      </Form.Group>
                      <Form.Group>
                        <TextInput
                          name="lastName"
                          id="lastName"
                          label="Last Name"
                          value={lastName}
                          onChange={e => setLastName(e.target.value)}
                          inputRef={register}
                          errors={errors.lastName}
                        />
                      </Form.Group>
                      <Form.Group className={props.enablePhone ? '' : 'mb-0'}>
                        <TextInput
                          name="email"
                          id="email"
                          label="Email"
                          value={email}
                          onChange={e => setEmail(e.target.value)}
                          inputRef={register}
                          errors={errors.email}
                        />
                      </Form.Group>

                      {props.enablePhone && <Form.Group className="mb-0">
                        <TextInput
                          name="phone"
                          id="phone"
                          label="Phone Number"
                          value={phone}
                          onChange={e => setPhone(e.target.value)}
                          inputRef={register}
                          errors={errors.phone}
                          InputProps={{
                            inputComponent: InputMaskPhone,
                          }}
                        />
                      </Form.Group>}
                    </Col>
                  </Row>
                  {props.isLoading ?
                   <Row className="form-button-container">
                    <div className="submit-loading-container">
                      <CircularProgress />
                    </div>
                    <p className="loader-content">Please wait a moment while we transfer your information to Legal & General to start your electronic application. It only takes about 15 minutes and no payment is required.</p>
                    </Row> :
                    <Row className="form-button-container">
                    <Col className="d-flex justify-content-center pr-2 pl-0">
                     <Button variant="outlined" className={classes.backButton} fullWidth size="medium" color="primary" onClick={props.onBack}>Back</Button>
                    </Col>
                    <Col className="d-flex justify-content-center pl-2 pr-0">
                     <CovrButton className={classes.submitButton} name="Continue" loading={loading} fullWidth disabled={disableContinueButton} />
                    </Col>
                    </Row>
                  }
                </Form>
                <Row className="disclaimer-container">
                  {props.quoterFormState.carrierName.toLowerCase() === 'haven' && <Form.Group className="agree-check mb-0">
                    <CheckboxInput
                      name="agreed"
                      id="agreed"
                      checked={agreed}
                      onChange={() => handleAgreeChecked()}
                      inputRef={register}
                      label="I Agree"
                      color="primary"
                    />
                  </Form.Group>}
                  {!props.isLoading && props.disclaimer}
                </Row>
              </div>
            </Col>
          </Row>
        </Container>
        {props.showCustomizeTermModal ? <TermProductDetailsDialog
          isOpen={isProductDetailsDialogOpen}
          productDetails={
            <ProductDetails
              carrierName={props.quoterFormState.selectedQuote.carrierName}
              productDescription={props.quoterFormState.selectedQuote.productDescription}
              hasPermConversion={props.quoterFormState.selectedQuote.hasConvertibility}
              productInformation={props.quoterFormState.selectedQuote.productInformation}
            />
          }
          quoteCardProps={props.quoterFormState.selectedQuote}
          hideSelect
          onClose={() => setIsProductDetailsDialogOpen(false)}
        /> :
          <ProductDetailsDialog
            isOpen={isProductDetailsDialogOpen}
            isHavenLifeSIProduct={props.isHavenLifeSIProduct}
            isHavenTermProduct={props.isHavenTermProduct}
            productDetails={
              <ProductDetails
                carrierName={props.quoterFormState.selectedQuote.carrierName}
                productDescription={props.quoterFormState.selectedQuote.productDescription}
                hasPermConversion={props.quoterFormState.selectedQuote.hasConvertibility}
                productInformation={props.quoterFormState.selectedQuote.productInformation}
              />
            }
            quoteCardProps={props.quoterFormState.selectedQuote}
            hideSelect
            onClose={() => setIsProductDetailsDialogOpen(false)}
          />
        }
      </div>
    </ThemeProvider>
  );
}

function ReviewContainer(props) {
  const { quote, planFeature, setIsProductDetailsDialogOpen } = props;
  const isLgaProduct = quote.selectedQuote.productID === QuoteProductID.LGA_PRODUCT_ID ;

  const classes = useStyles();
  const [currentFeatureStep, setCurrentFeatureStep] = useState(1);
  const planFeatureLength = planFeature.length;

  const setFeatureStep = () => {
    if ((currentFeatureStep + 1) > (planFeatureLength)) {
      setCurrentFeatureStep(1);
    } else {
      setCurrentFeatureStep(currentFeatureStep + 1);
    }
  };

  useEffect(() => {
    const slideTimeOut = props.isHavenLifeSIProduct ? 4000 : 4500;
    const timeout = setTimeout(setFeatureStep, slideTimeOut);

    return () => clearTimeout(timeout);
  }, [currentFeatureStep]);

  return (
    <Col md={6} className="review-container d-flex align-items-center">
      <div className="coverage-detail-container">
        <Row className="coverage-detail">
          <Col className="carrier-container d-flex justify-content-center align-items-center pr-0">
            <img src={quote.carrierLogoUrl} alt="Carrier Logo" className={(quote.carrierName.toLowerCase() === "lga") ? "lg" : ""}/>
            <p className={classes.ratingText}>{quote.selectedQuote.rating} Rating</p>
          </Col>
          <Col className="coverage-container d-flex justify-content-center align-items-start pl-0">
            <div className="coverage">
              <h2>Coverage Amount</h2>
              <p className="coverage-amount"><CurrencyFormat number={quote.coverageAmount} whole /></p>
              <p className="quote-amount">{`${CurrencyFormatFunc(quote.premium)}/mo`} <span>{quote.term || quote.termLength} Year Term</span></p>
              <button type="button" className={classes.detailButton} onClick={() => setIsProductDetailsDialogOpen(true)}>Approval Process</button>
            </div>
          </Col>
        </Row>
      </div>
      <div className="request-step-container">
        <h2 className="section-title text-center">Easy next steps</h2>
       <NextStep isHavenLifeSIProduct ={props.isHavenLifeSIProduct} isHavenTermProduct={props.isHavenTermProduct} isLgaProduct={isLgaProduct} carrierName={quote.carrierName}/>
      </div>
      <div className="plan-feature-container">
        <h2 className="section-title text-center">Your plan’s best features</h2>
        <div className="text-slideshow">
          <ul className={`step${currentFeatureStep}`}>
            {props.planFeature.map((f, index) => <FeatureCard planFeature={f} key={index}/>)}
          </ul>
        </div>
      </div>
    </Col>
  );
}

InsuranceRequest.propTypes = {
  quoterFormState: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  planFeature: PropTypes.array.isRequired,
  enablePhone: PropTypes.bool,
  onBack: PropTypes.func,
  isHavenLifeSIProduct: PropTypes.bool,
  isHavenTermProduct: PropTypes.bool,
  isLoading: PropTypes.bool,
  disclaimer: PropTypes.object,
  showCustomizeTermModal: PropTypes.bool,
  basicInfo: PropTypes.object,
};

ReviewContainer.propTypes = {
  planFeature: PropTypes.array.isRequired,
  isHavenLifeSIProduct: PropTypes.bool,
  isHavenTermProduct: PropTypes.bool,
  quote: PropTypes.object,
  setIsProductDetailsDialogOpen: PropTypes.func,
};

InsuranceRequest.DefaultFormState = (prefill = false, basicInfo = null) => {
  return {
    firstName: prefill ? 'Example' : (basicInfo && basicInfo.firstName) || '',
    lastName: prefill ? 'Consumer' : (basicInfo && basicInfo.lastName) || '',
    email: prefill ? 'user@address.com' : (basicInfo && basicInfo.email) || '',
    phone: prefill ? '(555) 555-1234' : (basicInfo && basicInfo.phone) || '',
  };
};



// WEBPACK FOOTER //
// ./src/components/Quoters/Common/InsuranceRequest/InsuranceRequest.js